// ── Reload-surviving generations ──────────────────────────────────────────────────────────────────────────────────────────
// A generation's HTTP request dies when the tab reloads (or the editor page is left), but the backend keeps processing and saves
// the finished image to history. An editor persists a lightweight marker (+ tiny preview) per in-flight job so that on its next
// mount it can restore the job's processing card in the recent slider and reconcile it against history. Shared by the AI photo
// editor and the Studio Editor — each with its own localStorage key.

/** A small marker store in localStorage under `key`: read / add / update / remove by `uid` (kept to the last `max`). */
export function createPendingJobStore(key, max = 12) {
    const read = () => {
        try { return JSON.parse(localStorage.getItem(key)) || []; } catch { return []; }
    };
    const write = (jobs) => {
        try { localStorage.setItem(key, JSON.stringify(jobs)); } catch { /* quota / disabled */ }
    };
    return {
        read,
        add(job) { write([...read().filter((j) => j.uid !== job.uid), job].slice(-max)); },
        update(uid, patch) { write(read().map((j) => (j.uid === uid ? { ...j, ...patch } : j))); },
        remove(uid) { write(read().filter((j) => j.uid !== uid)); },
    };
}

/** Downscale a (same-origin blob) image URL to a small JPEG data URL for the restored card thumbnail — small, survives a reload. */
export function makeThumbDataUrl(srcUrl, maxSize = 140) {
    return new Promise((resolve) => {
        try {
            const img = new window.Image();
            img.onload = () => {
                try {
                    const scale = Math.min(1, maxSize / Math.max(img.naturalWidth, img.naturalHeight));
                    const w = Math.max(1, Math.round(img.naturalWidth * scale));
                    const h = Math.max(1, Math.round(img.naturalHeight * scale));
                    const canvas = document.createElement('canvas');
                    canvas.width = w; canvas.height = h;
                    canvas.getContext('2d').drawImage(img, 0, 0, w, h);
                    resolve(canvas.toDataURL('image/jpeg', 0.5));
                } catch { resolve(null); }
            };
            img.onerror = () => resolve(null);
            img.src = srcUrl;
        } catch { resolve(null); }
    });
}
